import { useState } from 'react';
import type { FC, MouseEventHandler } from 'react';
import { FormattedMessage } from 'react-intl';
import { Button } from '@mui/material';
import { tss } from 'tss-react/mui';
import { useFragment, graphql } from 'react-relay';
import { getRelayEnvironment } from '@/RelayEnvironment';
import commitMutationPromise from '@/services/commitMutationPromise';
import Modal from '../Modal';
import { Paragraph } from '../Paragraph';
import DotsLoadingIndicator from '../DotsLoadingIndicator';
import type { ApproveDownloadDialog_survey$key } from './__generated__/ApproveDownloadDialog_survey.graphql';

const useStyles = tss.create(({ theme }) => ({
    modal: {
        height: '100vh',
        maxHeight: '100vh',
    },
    approveButton: {
        display: 'block',
        margin: `${theme.spacing(4)} auto`,
    },
}));
type ApproveDownloadDialogProps = {
    survey: ApproveDownloadDialog_survey$key;
    open: boolean;
    onClose(unlockedResults: boolean): void;
};

const unlockProjectSurveyResultsMutation = graphql`
    mutation ApproveDownloadDialog_UnlockProjectSurveyResultsMutation(
        $input: UnlockProjectSurveyResultsInput!
    ) {
        unlockProjectSurveyResults(input: $input) {
            survey {
                resultsUnlocked
            }
        }
    }
`;
const PriceListHref = 'https://binosight.com/pricing';
const PriceListLink = (
    <a href={PriceListHref} target="_blank" rel="noopener noreferrer">
        {PriceListHref}
    </a>
);

const ApproveDownloadDialog: FC<ApproveDownloadDialogProps> = (props) => {
    const { open, onClose } = props;
    const { classes } = useStyles();
    const [approving, setApproving] = useState(false);

    const survey = useFragment(surveyFragment, props.survey);

    const acceptAndDownload: MouseEventHandler = async (event) => {
        event.preventDefault();

        setApproving(true);

        await commitMutationPromise(getRelayEnvironment(), {
            mutation: unlockProjectSurveyResultsMutation,
            variables: {
                input: {
                    surveyId: survey.id,
                },
            },
        });
        setApproving(false);
        onClose(true);
    };

    return (
        <Modal
            open={open}
            closeModal={() => onClose(false)}
            className={classes.modal}
            header={{
                title: (
                    <FormattedMessage
                        defaultMessage="Accept download terms"
                        description="Approve download dialog"
                    />
                ),
            }}
        >
            <Paragraph>
                <FormattedMessage
                    defaultMessage="The first time a PQi Quality Report is downloaded, a cost will be charged based on the number of respondents who responded to the survey."
                    description="Approve download dialog"
                />
            </Paragraph>
            <Paragraph>
                <FormattedMessage
                    defaultMessage="If your organization has an agreement regarding the purchase of PQi Quality Reports, the actual price may differ slightly from Binosight's gross price list: {priceList}"
                    description="Approve download dialog"
                    values={{
                        priceList: PriceListLink,
                    }}
                />
            </Paragraph>
            <Paragraph>
                <FormattedMessage
                    defaultMessage="Downloading this quality report also unlocks the measurement results for deeper analysis in the service and also makes it possible to download the PQi Feedback report free of charge."
                    description="Approve download dialog"
                />
            </Paragraph>
            <Button
                onClick={acceptAndDownload}
                variant="contained"
                className={classes.approveButton}
                disabled={approving}
            >
                <FormattedMessage
                    defaultMessage="Accept and download"
                    description="Approve download dialog"
                />
                {approving && <DotsLoadingIndicator />}
            </Button>
        </Modal>
    );
};
const surveyFragment = graphql`
    fragment ApproveDownloadDialog_survey on ProjectSurvey {
        id
        resultsUnlocked
    }
`;

export default ApproveDownloadDialog;
